/**
 * `History<S, Cmd, Event>` — the per-actor undo/redo authority, once, for every
 * command domain. The four near-identical histories (roster, game, encounter,
 * presence) collapse onto it: the *machinery* wrapped around the pure reducer —
 * per-actor undo/redo stacks, apply→record-inverse, reject-stale-as-no-op, the
 * serializable snapshot — lives here and only here.
 *
 * **Parameterized on a *bound* reducer.** The generic never sees `ApplyCtx` or
 * the compendium: the constructor takes an `apply: (state, command) =>
 * ApplyResult<S, Cmd, Event>` that has already closed over whatever read-only
 * context (the compendium) the domain needs. So the four domains bind their
 * reducer once (roster/encounter thread `ctx.compendium`, game/presence thread
 * nothing) and the base is reference-data-agnostic. This mirrors exactly the way
 * `inverseRoundTrip.property.test.ts` parameterizes its property over each
 * domain's bound `apply` — the proof that runner stands behind is the proof this
 * extraction stands behind.
 *
 * **Home (NOT `shared/`).** It lives in `@lancer-vtt/domain` because three of
 * the four histories (roster/encounter/game) already do, and the fourth's driver
 * (`backend/src/history.ts`, presence) already depends on `@lancer-vtt/domain`.
 * It needs only `ApplyResult` + `CommandError` from `@lancer-vtt/shared` (both
 * imported below), never any domain `Command` type — so no cycle. A standalone
 * `command-core` package would be ceremony for one file.
 *
 * **Per-actor undo.** The stacks are partitioned by the `actorId`
 * of whoever issued the command, so each participant undoes their own edits
 * independently.
 *
 * **No rebasing (the deferred-OT seam).** When an actor's stored inverse no
 * longer applies cleanly (another actor's intervening edit moved the state out
 * from under it and the bound reducer throws `CommandError`), the undo is
 * rejected as a no-op — the inverse is discarded, nothing is broadcast — rather
 * than rebased. Last-write-wins plus reject-stale-undo is the chosen
 * reconciliation; OT/CRDT is explicitly deferred.
 *
 * **Extension seam.** Subclasses that have *genuine* extra behavior (the
 * encounter forward-log / replay) override {@link History.onApplied}, the
 * post-apply hook that runs only after a clean `applyCommand` — it never fires on
 * a rejected command or on undo/redo. The base calls it nowhere else, so the
 * default (a no-op) keeps every other domain a pure thin binding.
 *
 * **Outcome batching.** {@link
 * applyDetached} + {@link pushInverse} are an additive, opt-in pair alongside
 * `applyCommand`: detach applies a command (state + `onApplied` fire, same as
 * `applyCommand`) without pushing its own inverse, and pushInverse commits ONE
 * pre-composed inverse (typically a `batch` of the collected detached inverses,
 * reversed) as a single undo unit. A domain that never calls them keeps
 * `applyCommand`'s one-call-one-undo-entry behavior byte-identical.
 */
import type { ApplyResult, CommandError, Result } from "@lancer-vtt/shared";
import { ok } from "@lancer-vtt/shared";

/**
 * A reducer already bound to its read-only context: applies one command to the
 * state and returns a {@link Result} — `ok` of the new state + inverse + events,
 * or `err(commandError)` on an *expected* rejection (never a throw). The domain
 * closes its `ApplyCtx`/compendium into this — the generic
 * stays reference-data-agnostic.
 */
export type BoundReducer<S, Cmd, Event> = (
  state: S,
  command: Cmd,
) => Result<ApplyResult<S, Cmd, Event>, CommandError>;

/** Undo/redo availability for one actor, derived purely from its stacks. */
export type UndoState = {
  canUndo: boolean;
  canRedo: boolean;
  /** The `type` of the command the next undo would reverse, if any. */
  undoLabel: string | null;
  /** The `type` of the command the next redo would re-apply, if any. */
  redoLabel: string | null;
}

/** One actor's undo + redo stacks (inverse / forward commands). */
export type ActorStacks<Cmd> = {
  /** Inverses to walk this actor one step backward. Top = next undo. */
  undo: Cmd[];
  /** Forward commands to re-apply. Top = next redo. */
  redo: Cmd[];
}

const EMPTY_UNDO: UndoState = {
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
};

/**
 * The composite stack key `(actorId, scope)`. When the scope
 * is the default empty string the key is **exactly the bare `actorId`** — so every
 * domain whose `scopeOf` returns `""` (game/encounter/presence, and roster's own
 * unscoped path) keeps bare-`actorId` map keys + snapshot bytes. A
 * non-empty scope appends a `NUL`-delimited suffix (a separator that can't occur in
 * a slot id or a `mech:`/`pilot:` scope string).
 */
function stackKey(actorId: string, scope: string): string {
  return scope === "" ? actorId : `${actorId}\u0000${scope}`;
}

/**
 * In-memory per-actor command authority: a `state` of type `S` + per-actor
 * undo/redo stacks, every change routed through the bound reducer. The mutating
 * calls (`applyCommand`/`undo`/`redo`) return the reducer's `events` (empty on a
 * no-op) so a transport driver (the ws room) can broadcast them; the solo
 * drivers ignore the return and just read `state`.
 */
export class History<S, Cmd extends { type: string }, Event> {
  state: S;
  /**
   * Per-`(actorId, scope)` undo/redo stacks, keyed by {@link stackKey}. With the
   * default scope (`""`) the key is the bare `actorId`.
   */
  private readonly stacks = new Map<string, ActorStacks<Cmd>>();
  /** The reducer with its read-only context already bound in. */
  protected readonly reducer: BoundReducer<S, Cmd, Event>;

  constructor(
    reducer: BoundReducer<S, Cmd, Event>,
    state: S,
    history: Record<string, ActorStacks<Cmd>> = {},
  ) {
    this.reducer = reducer;
    this.state = state;
    // Keys are the composite `(actorId, scope)` strings (bare `actorId` at the
    // default scope) — opaque here, round-tripped verbatim from `historySnapshot()`.
    for (const [key, s] of Object.entries(history)) {
      this.stacks.set(key, { undo: [...s.undo], redo: [...s.redo] });
    }
  }

  /**
   * The scope a command's undo lives in — the sub-partition *within* an actor.
   * The base is a single constant scope (`""`), so every unscoped domain
   * (game/encounter/presence) keeps exactly one stack per actor;
   * `RosterHistory` **overrides** this
   * with its mech-vs-pilot rule. Public so a transport driver (the ws room) can stamp
   * the affected scope onto its per-scope `undo_state` push without re-deriving it.
   */
  scopeOf(_command: Cmd): string {
    return "";
  }

  private stacksFor(actorId: string, scope: string): ActorStacks<Cmd> {
    const key = stackKey(actorId, scope);
    let s = this.stacks.get(key);
    if (s === undefined) {
      s = { undo: [], redo: [] };
      this.stacks.set(key, s);
    }
    return s;
  }

  /**
   * Post-apply hook, run **only** after a clean `applyCommand` (never on a
   * rejected command, never on undo/redo). The default is a no-op so every plain
   * binding stays thin; the encounter history overrides it to append its retained
   * forward log. Receives the command that was just applied and its issuing actor.
   */
  protected onApplied(_command: Cmd, _actorId: string): void {}

  /**
   * Apply a user-driven command on behalf of `actorId`: commit the new state,
   * record its inverse on that actor's undo stack, discard that actor's redo
   * future. Returns `ok(events)` (the reducer's events to broadcast) on success,
   * or the reducer's `err(commandError)` — **without touching state or any
   * stack**, so a rejected command is a clean no-op (the no-op-before-mutate
   * guarantee is structural: state is assigned only behind `result.ok`).
   * A genuine bug still throws out of the reducer and propagates.
   */
  applyCommand(command: Cmd, actorId: string): Result<Event[], CommandError> {
    const result = this.reducer(this.state, command);
    if (!result.ok) return result;
    this.state = result.value.state;
    const s = this.stacksFor(actorId, this.scopeOf(command));
    s.undo.push(result.value.inverse);
    s.redo.length = 0;
    // Runs only after a clean apply — a rejected command returns above and never
    // reaches here. The default is a no-op; the encounter history overrides it.
    this.onApplied(command, actorId);
    return ok(result.value.events);
  }

  /**
   * Apply a command exactly as {@link applyCommand} does — commit the reducer's new
   * state and still run {@link onApplied} — but do **not** push an inverse onto any
   * undo stack or touch redo. Returns the inverse alongside the events so a caller
   * can compose several detached applies into ONE **composite** undo unit via
   * {@link pushInverse} (outcome batching). On a reducer rejection, returns the `err` untouched — exactly like
   * `applyCommand`, state is never assigned outside `result.ok`.
   *
   * **Additive and opt-in.** This is a new entry point alongside `applyCommand`,
   * never called from it — a domain that never reaches for `applyDetached`/
   * `pushInverse` keeps `applyCommand`'s stack-per-call behavior byte-identical.
   */
  applyDetached(
    command: Cmd,
    actorId: string,
  ): Result<{ events: Event[]; inverse: Cmd }, CommandError> {
    const result = this.reducer(this.state, command);
    if (!result.ok) return result;
    this.state = result.value.state;
    // Same post-apply hook as `applyCommand` — the forward log must not notice the
    // difference between N individually-pushed applies and N detached ones folded
    // into one composite undo.
    this.onApplied(command, actorId);
    return ok({ events: result.value.events, inverse: result.value.inverse });
  }

  /**
   * Push a pre-composed inverse — typically a `batch` of several {@link
   * applyDetached} inverses, reversed into undo order — onto `actorId`'s undo stack
   * for `scope` (default `""`), clearing that stack's redo exactly once. This is
   * the composite half of outcome batching: a caller detaches N applies, collects
   * their inverses in its own local scope, then calls this ONE time at the end of
   * the walk so the whole outcome undoes/redoes as a single unit. Whether to call
   * this at all (an empty walk shouldn't) is the caller's decision — this method
   * itself does not special-case an empty inverse.
   */
  pushInverse(inverse: Cmd, actorId: string, scope = ""): void {
    const s = this.stacksFor(actorId, scope);
    s.undo.push(inverse);
    s.redo.length = 0;
  }

  /**
   * Undo the actor's last command. Returns `ok(events)` to broadcast, or `ok([])`
   * (a no-op) when there is nothing to undo *or* when the stored inverse no longer
   * applies cleanly (rejected-as-no-op, not rebased — the deferred-OT seam). A
   * rejected inverse is discarded from the stack and nothing is broadcast.
   *
   * The stale-inverse rejection is treated as a **successful no-op**, not an
   * `err`: under LWW + reject-stale a stale inverse is silently dropped — the undo
   * "succeeds" by doing nothing. A genuine bug still throws out of the reducer and
   * propagates. Returns `Result` for signature uniformity with `applyCommand`.
   *
   * `scope` selects the sub-partition to walk back (default `""`, the single scope
   * every unscoped domain uses); a roster window passes its own `mech:`/`pilot:`
   * scope so an undo only reverses that subject's edits.
   */
  undo(actorId: string, scope = ""): Result<Event[], CommandError> {
    const s = this.stacks.get(stackKey(actorId, scope));
    const inverse = s?.undo.pop();
    if (inverse === undefined) return ok([]);
    const result = this.reducer(this.state, inverse);
    // Stale inverse → no-op, discard (already popped), broadcast nothing.
    if (!result.ok) return ok([]);
    this.state = result.value.state;
    s!.redo.push(result.value.inverse);
    return ok(result.value.events);
  }

  /**
   * Peek the top of `(actorId, scope)`'s undo stack WITHOUT popping it (additive/opt-in):
   * a caller that needs to inspect — never mutate — the
   * inverse a real {@link undo} would walk next, e.g. to check "is the shared stack's top
   * entry mine" before deciding whether a narrower actor (a player) may reach for the
   * stack a `roleOf`-gated room otherwise reserves for its GM/full-access caller. Returns
   * `undefined` when the stack is empty or has never been touched. Never called from
   * `undo`/`redo`/`applyCommand` — a domain that never reaches for it sees no behavior
   * change at all.
   */
  peekUndo(actorId: string, scope = ""): Cmd | undefined {
    const s = this.stacks.get(stackKey(actorId, scope));
    return s === undefined || s.undo.length === 0 ? undefined : s.undo[s.undo.length - 1];
  }

  /** Mirror of {@link peekUndo} for the redo stack. */
  peekRedo(actorId: string, scope = ""): Cmd | undefined {
    const s = this.stacks.get(stackKey(actorId, scope));
    return s === undefined || s.redo.length === 0 ? undefined : s.redo[s.redo.length - 1];
  }

  /**
   * Merge the top two entries of `(actorId, scope)`'s undo stack into `merged`, **only** when they
   * are exactly `lower` then `upper` (by identity), so one undo walks both. Returns whether it
   * merged; anything else on top (another actor's entry landed in between) leaves the stack as it
   * is. Redo is untouched: both entries were pushed by applies that already cleared it. Additive and
   * opt-in, like {@link pushInverse}: the encounter room's auto-activation fold uses it to make the
   * folded `activate_unit` and the action it rides on one undo step.
   */
  mergeTopUndo(args: { actorId: string; scope?: string; lower: Cmd; upper: Cmd; merged: Cmd }): boolean {
    const s = this.stacks.get(stackKey(args.actorId, args.scope ?? ""));
    const n = s?.undo.length ?? 0;
    if (s === undefined || n < 2 || s.undo[n - 1] !== args.upper || s.undo[n - 2] !== args.lower) return false;
    s.undo.splice(n - 2, 2, args.merged);
    return true;
  }

  /** Redo the actor's last undone command in `scope` (default `""`). Mirror of {@link undo}. */
  redo(actorId: string, scope = ""): Result<Event[], CommandError> {
    const s = this.stacks.get(stackKey(actorId, scope));
    const forward = s?.redo.pop();
    if (forward === undefined) return ok([]);
    const result = this.reducer(this.state, forward);
    // Stale forward → no-op, discard (already popped), broadcast nothing.
    if (!result.ok) return ok([]);
    this.state = result.value.state;
    s!.undo.push(result.value.inverse);
    return ok(result.value.events);
  }

  /** One actor's undo/redo availability + next-command labels, for the given `scope` (default `""`). */
  undoState(actorId: string, scope = ""): UndoState {
    const s = this.stacks.get(stackKey(actorId, scope));
    if (s === undefined) return EMPTY_UNDO;
    const top = (stack: Cmd[]) =>
      stack.length > 0 ? stack[stack.length - 1]! : null;
    const u = top(s.undo);
    const r = top(s.redo);
    return {
      canUndo: s.undo.length > 0,
      canRedo: s.redo.length > 0,
      undoLabel: u ? u.type : null,
      redoLabel: r ? r.type : null,
    };
  }

  /** Serialize the per-actor history for persistence (the CLI's file backing). */
  historySnapshot(): Record<string, ActorStacks<Cmd>> {
    const out: Record<string, ActorStacks<Cmd>> = {};
    for (const [actor, s] of this.stacks.entries()) {
      out[actor] = { undo: [...s.undo], redo: [...s.redo] };
    }
    return out;
  }
}
